import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';

function AboutHero() {
  return (
    <section className="relative w-full h-[320px] md:h-[400px] flex items-center justify-center overflow-hidden rounded-xl mb-10" aria-labelledby="about-hero-title">
      <img
        src="https://readdy.ai/api/search-image?query=Modern%20media%20production%20studio%20with%20professional%20video%20editing%20setup%2C%20warm%20cinematic%20lighting%2C%20creative%20workspace%20atmosphere%2C%20multiple%20monitors%20displaying%20film%20footage%2C%20sleek%20contemporary%20design%2C%20artistic%20environment&width=1600&height=600&seq=about-hero-bg&orientation=landscape"
        alt="91高清视频 - 关于我们"
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60"></div>
      <div className="relative z-10 text-center px-4">
        <h1 id="about-hero-title" className="text-2xl md:text-4xl font-bold text-background-50 font-heading">
          关于我们
        </h1>
        <p className="mt-3 text-sm md:text-base text-background-200 max-w-[600px] mx-auto">
          致力于为广大用户提供优质的高清视频在线观看体验
        </p>
      </div>
    </section>
  );
}

function ContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    website_alt: '',
  });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [statusMessage, setStatusMessage] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const honeypotValue = (e.currentTarget.elements.namedItem('website_alt') as HTMLInputElement)?.value?.trim();
    if (honeypotValue) {
      setStatus('success');
      setStatusMessage('感谢您的留言，我们会尽快回复！');
      setFormData({ name: '', email: '', subject: '', message: '', website_alt: '' });
      return;
    }

    if (formData.message.length > 500) {
      setStatus('error');
      setStatusMessage('留言内容不能超过500个字符');
      return;
    }

    setStatus('loading');

    try {
      const formPayload = new FormData(e.currentTarget);
      const res = await fetch('https://readdy.ai/api/form/d9nh6h8bdkpk99j7gjfg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formPayload as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (res.ok && parsed?.code === 'OK') {
        setStatus('success');
        setStatusMessage('感谢您的留言，我们会尽快回复！');
        setFormData({ name: '', email: '', subject: '', message: '', website_alt: '' });
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后重试';
        setStatus('error');
        setStatusMessage(serverMsg);
      }
    } catch {
      setStatus('error');
      setStatusMessage('网络错误，请检查连接后重试');
    }
  };

  return (
    <form
      id="contact-form"
      data-readdy-form=""
      onSubmit={handleSubmit}
      className="space-y-4"
      noValidate
    >
      <div>
        <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-800 mb-1.5">
          您的姓名 <span className="text-primary-500">*</span>
        </label>
        <input
          id="contact-name"
          type="text"
          name="name"
          value={formData.name}
          onChange={handleChange}
          required
          className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-lg bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors"
          placeholder="请输入您的姓名"
        />
      </div>
      <div>
        <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-800 mb-1.5">
          电子邮箱 <span className="text-primary-500">*</span>
        </label>
        <input
          id="contact-email"
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          required
          className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-lg bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors"
          placeholder="请输入您的邮箱"
        />
      </div>
      <div>
        <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-800 mb-1.5">
          主题 <span className="text-primary-500">*</span>
        </label>
        <input
          id="contact-subject"
          type="text"
          name="subject"
          value={formData.subject}
          onChange={handleChange}
          required
          className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-lg bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors"
          placeholder="请输入主题"
        />
      </div>
      <div>
        <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-800 mb-1.5">
          留言内容 <span className="text-primary-500">*</span>
        </label>
        <textarea
          id="contact-message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          required
          maxLength={500}
          rows={5}
          className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-lg bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors resize-none"
          placeholder="请输入您的留言（500字以内）"
        ></textarea>
        <p className="text-xs text-foreground-400 mt-1">{formData.message.length}/500</p>
      </div>

      <div className="anti-spam-field">
        <input
          type="text"
          name="website_alt"
          value={formData.website_alt}
          onChange={handleChange}
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          readOnly
        />
      </div>

      <button
        type="submit"
        disabled={status === 'loading'}
        className="w-full px-6 py-3 bg-primary-500 text-background-50 rounded-lg font-medium text-sm hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
      >
        {status === 'loading' ? (
          <span className="flex items-center justify-center gap-2">
            <i className="ri-loader-4-line animate-spin"></i>
            提交中...
          </span>
        ) : (
          '提交留言'
        )}
      </button>

      {status === 'success' && (
        <div className="p-3 rounded-lg bg-accent-100 text-accent-800 text-sm flex items-center gap-2">
          <i className="ri-checkbox-circle-line"></i>
          {statusMessage}
        </div>
      )}
      {status === 'error' && (
        <div className="p-3 rounded-lg bg-primary-100 text-primary-700 text-sm flex items-center gap-2">
          <i className="ri-error-warning-line"></i>
          {statusMessage}
        </div>
      )}
    </form>
  );
}

export default function AboutPage() {
  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <div className="max-w-[1200px] mx-auto px-4 md:px-6 pb-14">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />

          <AboutHero />

          <section className="mb-14" aria-labelledby="brand-intro-title">
            <h2 id="brand-intro-title" className="text-xl md:text-2xl font-bold text-foreground-950 font-heading mb-5">
              品牌介绍
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  <strong>91高清视频</strong>成立于2020年，是一家专注于高清视频在线观看的综合性视频平台。我们致力于为广大用户提供
                  <strong>超清视频在线观看</strong>不卡顿、<strong>高清影视在线</strong>畅享清晰画质的优质观影体验。
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  平台汇聚了海量<strong>国产高清视频</strong>内容，涵盖电影、电视剧、综艺、动漫、纪录片等多个品类，每日持续更新热门影视资源。
                  我们的技术团队不断优化视频分发网络和播放引擎，确保用户在各类设备上都能享受<strong>高清视频在线播放</strong>的流畅体验。
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  经过多年的发展，91高清视频已积累了超过5000万活跃用户，日均视频播放量突破1亿次，
                  成为国内领先的<strong>高清在线</strong>视频平台之一。我们始终坚持"内容为王、用户至上"的理念，持续为用户创造价值。
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { value: '5000万+', label: '活跃用户' },
                  { value: '10万+', label: '视频资源' },
                  { value: '1亿+', label: '日播放量' },
                  { value: '99.9%', label: '服务可用率' },
                ].map((stat) => (
                  <div key={stat.label} className="p-5 rounded-lg bg-background-100 text-center">
                    <p className="text-2xl md:text-3xl font-bold text-primary-500 font-heading">{stat.value}</p>
                    <p className="text-xs text-foreground-500 mt-1">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="mb-14" aria-labelledby="reputation-title">
            <h2 id="reputation-title" className="text-xl md:text-2xl font-bold text-foreground-950 font-heading mb-5">
              品牌信誉
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                {
                  icon: 'ri-shield-check-line',
                  title: '正版授权',
                  desc: '平台所有视频内容均获得正版授权或合法来源，严格遵守知识产权相关法律法规，保障内容创作者权益。',
                },
                {
                  icon: 'ri-lock-line',
                  title: '数据安全',
                  desc: '采用行业领先的加密技术保护用户数据，通过ISO 27001信息安全认证，全方位保障用户隐私安全。',
                },
                {
                  icon: 'ri-customer-service-2-line',
                  title: '优质服务',
                  desc: '7×24小时在线客服支持，快速响应用户反馈。专业技术团队保障平台稳定运行，服务可用率高达99.9%。',
                },
                {
                  icon: 'ri-award-line',
                  title: '行业认可',
                  desc: '连续三年获得"年度最佳视频平台"称号，入选中国互联网协会"优质内容平台"推荐名单。',
                },
                {
                  icon: 'ri-thumb-up-line',
                  title: '用户信赖',
                  desc: '累计超过5000万用户的信赖选择，用户满意度评分长期保持在4.8分以上，口碑行业领先。',
                },
                {
                  icon: 'ri-eth-line',
                  title: '技术创新',
                  desc: '自主研发的智能视频编码和CDN分发技术，实现超清画质与流畅播放的完美平衡，获得多项技术专利。',
                },
              ].map((item) => (
                <div key={item.title} className="p-5 rounded-lg border border-background-200 bg-background-50 hover:border-primary-200 transition-colors">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 mb-3">
                    <i className={`${item.icon} text-lg`}></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-2">{item.title}</h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </section>

          <section aria-labelledby="contact-title">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div>
                <h2 id="contact-title" className="text-xl md:text-2xl font-bold text-foreground-950 font-heading mb-5">
                  联系我们
                </h2>
                <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                  如果您有任何关于<strong>高清视频在线观看</strong>的问题、建议或合作意向，欢迎通过以下方式联系我们。
                  我们的客服团队将在24小时内回复您的咨询。
                </p>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className="ri-mail-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">电子邮箱</p>
                      <p className="text-sm text-foreground-500">contact@91hdvideo.com</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className="ri-phone-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">客服热线</p>
                      <p className="text-sm text-foreground-500">400-888-9999</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className="ri-map-pin-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">公司地址</p>
                      <p className="text-sm text-foreground-500">北京市朝阳区建国路88号SOHO现代城A座18层</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className="ri-time-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">工作时间</p>
                      <p className="text-sm text-foreground-500">周一至周日 9:00 - 21:00</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-background-100 rounded-lg p-5 md:p-6">
                <h3 className="text-base font-semibold text-foreground-900 mb-4 font-heading">
                  在线留言
                </h3>
                <ContactForm />
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: '关于我们 - 91高清视频',
            description: '91高清视频是一家专注于高清视频在线观看的综合性视频平台，提供超清视频在线观看和高清影视在线服务。',
            url: 'https://www.example.com/about',
            isPartOf: {
              '@type': 'WebSite',
              name: '91高清视频',
              url: 'https://www.example.com',
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.example.com/' },
              { '@type': 'ListItem', position: 2, name: '关于我们' },
            ],
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: '91高清视频',
            url: 'https://www.example.com',
            description: '高清视频在线观看平台，提供超清画质视频内容',
            contactPoint: {
              '@type': 'ContactPoint',
              telephone: '400-888-9999',
              contactType: 'customer service',
              availableLanguage: 'Chinese',
            },
            address: {
              '@type': 'PostalAddress',
              streetAddress: '建国路88号SOHO现代城A座18层',
              addressLocality: '北京',
              addressRegion: '朝阳区',
              addressCountry: 'CN',
            },
          }),
        }}
      />
    </>
  );
}